import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const BLINK_INTERVAL_MS = 1200
const FAVICON_SIZE = 64
const BADGE_RADIUS = FAVICON_SIZE * 0.32
const BADGE_COLOR = '#ef4444'
const MAX_BADGE_COUNT = 9

// Captured the first time the hook runs, before anything is changed, so
// every later run restores to what index.html shipped with.
let originalTitle: string | null = null
let originalFavicon: { href: string; type: string } | null = null
let baseFaviconImage: Promise<HTMLImageElement | null> | null = null

function getFaviconLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
}

function loadBaseFavicon(href: string): Promise<HTMLImageElement | null> {
  baseFaviconImage ??= new Promise((resolve) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = href
  })
  return baseFaviconImage
}

export function formatBadgeCount(count: number): string {
  return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count)
}

async function renderBadgedFavicon(href: string, count: number): Promise<string | null> {
  const image = await loadBaseFavicon(href)
  const canvas = document.createElement('canvas')
  canvas.width = FAVICON_SIZE
  canvas.height = FAVICON_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  if (image) ctx.drawImage(image, 0, 0, FAVICON_SIZE, FAVICON_SIZE)

  const label = formatBadgeCount(count)
  const cx = FAVICON_SIZE - BADGE_RADIUS
  const cy = BADGE_RADIUS

  ctx.beginPath()
  ctx.arc(cx, cy, BADGE_RADIUS - 2, 0, 2 * Math.PI)
  ctx.fillStyle = BADGE_COLOR
  ctx.fill()
  ctx.lineWidth = 4
  ctx.strokeStyle = '#ffffff'
  ctx.stroke()

  ctx.fillStyle = '#ffffff'
  ctx.font = `700 ${label.length > 1 ? BADGE_RADIUS * 0.95 : BADGE_RADIUS * 1.25}px Inter, Arial, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, cx, cy + 2)

  try {
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

function restoreTab(link: HTMLLinkElement | null) {
  if (originalTitle !== null) document.title = originalTitle
  if (link && originalFavicon) {
    link.type = originalFavicon.type
    link.href = originalFavicon.href
  }
}

// Mirrors the unread count onto the browser tab: a red badge on the
// favicon and "(n) Title" while there's anything pending, and — only while
// the tab is in the background — the title alternates with a "new message"
// line so it catches the eye in a row of tabs. Coming back to the tab stops
// the blinking; the count stays until the conversations are actually read.
export function useTabAttention(unreadCount: number): void {
  const { t } = useTranslation()
  const alertLabel = t('attention.tab_title', { count: unreadCount })

  useEffect(() => {
    const link = getFaviconLink()
    originalTitle ??= document.title
    if (link) originalFavicon ??= { href: link.href, type: link.type }

    if (unreadCount === 0) {
      restoreTab(link)
      return
    }

    let cancelled = false
    if (link && originalFavicon) {
      void renderBadgedFavicon(originalFavicon.href, unreadCount).then((dataUrl) => {
        if (cancelled || !dataUrl) return
        link.type = 'image/png'
        link.href = dataUrl
      })
    }

    const countedTitle = `(${unreadCount}) ${originalTitle}`
    const alertTitle = `💬 ${alertLabel}`
    let timer: number | undefined
    let showingAlert = false

    const stopBlinking = () => {
      window.clearInterval(timer)
      timer = undefined
      document.title = countedTitle
    }

    const startBlinking = () => {
      if (timer !== undefined) return
      showingAlert = true
      document.title = alertTitle
      timer = window.setInterval(() => {
        showingAlert = !showingAlert
        document.title = showingAlert ? alertTitle : countedTitle
      }, BLINK_INTERVAL_MS)
    }

    const syncWithVisibility = () => {
      if (document.visibilityState === 'hidden') startBlinking()
      else stopBlinking()
    }

    syncWithVisibility()
    document.addEventListener('visibilitychange', syncWithVisibility)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', syncWithVisibility)
    }
  }, [unreadCount, alertLabel])

  // Leaving the private area (e.g. signing out) must not strand a badge.
  useEffect(() => () => restoreTab(getFaviconLink()), [])
}
